import { useEffect, useRef, useState } from 'react';

export function useScrollAnimation() {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const elementRef = useRef<HTMLElement>(null);
  const rafRef = useRef<number | undefined>(undefined);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      // Используем requestAnimationFrame для оптимизации
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = requestAnimationFrame(() => {
        const currentScrollY = window.scrollY;
        
        // Обновляем только если изменение значительное (больше 5px)
        if (Math.abs(currentScrollY - lastScrollY.current) > 5) {
          lastScrollY.current = currentScrollY;
          setScrollY(currentScrollY);
        }
        
        if (elementRef.current) {
          const rect = elementRef.current.getBoundingClientRect();
          const isInView = rect.top < window.innerHeight * 0.8 && rect.bottom > 0;
          setIsVisible(isInView);
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  return { isVisible, scrollY, elementRef };
}

export function useParallax(speed: number = 0.5) {
  const [offset, setOffset] = useState(0);
  const elementRef = useRef<HTMLElement>(null);
  const rafRef = useRef<number | undefined>(undefined);
  const lastOffset = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      // Используем requestAnimationFrame для оптимизации
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = requestAnimationFrame(() => {
        if (elementRef.current) {
          const rect = elementRef.current.getBoundingClientRect();
          const elementTop = rect.top + window.scrollY;
          const scrollPosition = window.scrollY;
          const parallaxOffset = (scrollPosition - elementTop) * speed;
          
          // Обновляем только если изменение значительное (больше 1px)
          if (Math.abs(parallaxOffset - lastOffset.current) > 1) {
            lastOffset.current = parallaxOffset;
            setOffset(parallaxOffset);
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [speed]);

  return { offset, elementRef };
}

